import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  DocumentPlusIcon
} from '@heroicons/react/24/outline';
import InvoiceDashboard from './InvoiceDashboard';
import InvoiceList from './InvoiceList';
import CompanyManagement from './CompanyManagement';
import PartnerManagement from './PartnerManagement';
import CreditNoteList from './CreditNoteList';
import CreditNoteCreate from './CreditNoteCreate';
import DebitNoteList from './DebitNoteList';
import DebitNoteCreate from './DebitNoteCreate';
import Reports from './Reports';
import IncomingInvoices from './IncomingInvoices';

const Invoices = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: t('invoices.dashboard'), icon: DocumentTextIcon },
    { id: 'list', label: t('invoices.invoiceList'), icon: DocumentTextIcon },
    { id: 'company', label: t('invoices.company'), icon: BuildingOfficeIcon },
    { id: 'partners', label: t('invoices.partners'), icon: UserGroupIcon },
    { id: 'credit-notes', label: t('invoices.creditNotes'), icon: DocumentTextIcon },
    { id: 'debit-notes', label: t('invoices.debitNotes'), icon: DocumentTextIcon },
    { id: 'reports', label: t('invoices.reports'), icon: DocumentTextIcon },
    { id: 'incoming', label: t('invoices.incomingInvoices'), icon: DocumentPlusIcon }
  ];

  // Handle tab from URL parameter
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabs.find(tab => tab.id === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange = (tabId, params = {}) => {
    setActiveTab(tabId);
    const searchParamsObj = { tab: tabId, ...params };
    setSearchParams(searchParamsObj);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <InvoiceDashboard onTabChange={handleTabChange} />;
      case 'list':
        return <InvoiceList />;
      case 'credit-notes':
        return <CreditNoteList onTabChange={handleTabChange} />;
      case 'credit-note-create':
        return <CreditNoteCreate onTabChange={handleTabChange} />;
      case 'debit-notes':
        return <DebitNoteList onTabChange={handleTabChange} />;
      case 'debit-note-create':
        return <DebitNoteCreate onTabChange={handleTabChange} />;
      case 'reports':
        return <Reports />;
      case 'incoming':
        return <IncomingInvoices />;
      case 'company':
        return <CompanyManagement />;
      case 'partners':
        return <PartnerManagement />;
      default:
        return <InvoiceDashboard onTabChange={handleTabChange} />;
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('invoices.financeDashboard')}</h1>
          <p className="text-gray-600 mt-2">{t('invoices.manageInvoicesCompanyPartners')}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </div>
    </div>
  );
};





export default Invoices; 